import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import IconButton from "@mui/material/IconButton";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { alpha, } from "@mui/material/styles";
import Box from '@mui/material/Box';
import { Badge } from "@mui/material";
import { BellIcon, BellOffIcon } from 'lucide-react';

const Notification = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const listRef = useRef(null);
    const { user } = useSelector(state => state.authen);
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const toggleDropdown = () => {
        setUnreadCount(0);
        setOpen((prev) => !prev);
    };

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        if (!user?.accountId) return;

        const eventSource = new EventSource(`${process.env.REACT_APP_BASE_URL}/alert/notifications/${user.accountId}`);

        eventSource.onmessage = (event) => {
            const data = event.data?.trim();
            if (!data || data.startsWith(':')) return;
            try {
                const data = JSON.parse(event.data);

                const newNotif = {
                    id: Date.now(),
                    message: `🔔 Someone opened '${data.key}' (${data.type}) from ${data.country} on ${data.device} at ${new Date(data.timestamp || Date.now()).toLocaleString()}`
                };

                setNotifications((prev) => [newNotif, ...prev]);
                setUnreadCount((prev) => prev + 1);

                setTimeout(() => {
                    if (listRef.current) {
                        listRef.current.firstElementChild?.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            } catch (err) {
                console.error('Error parsing SSE data:', err);
            }
        };

        eventSource.onerror = (err) => {
            console.error('SSE connection error:', err);
        };

        return () => {
            eventSource.close();
        };
    }, [user?.accountId]);

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box>
                <IconButton
                    ref={anchorRef}
                    onClick={toggleDropdown}
                    aria-label="notifications"
                    sx={{
                        color: notifications.length ? "#F59E0B" : "#9CA3AF",
                        "&:hover": { backgroundColor: alpha("#F59E0B", 0.1) },
                    }}
                >
                    <Badge
                        badgeContent={unreadCount}
                        color="error"
                        invisible={unreadCount === 0}
                        overlap="circular"
                        sx={{ "& .MuiBadge-badge": { fontSize: "0.7rem" } }}
                    >
                        {notifications.length ? <BellIcon/>  : <BellOffIcon />}
                    </Badge>
                </IconButton>

                <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" style={{ zIndex: 1300 }}>
                    <Paper sx={{ width: 300, maxHeight: 300, overflowY: 'auto', p: 1 }} className='no-scrollbar'>
                        <ul ref={listRef} style={{ padding: 0, margin: 0, listStyle: "none" }}>
                            {notifications.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                    No notifications yet.
                                </Typography>
                            ) : (
                                notifications.map((notif) => (
                                    <li key={notif.id} style={{
                                        padding: '0.5rem',
                                        marginBottom: '0.5rem',
                                        backgroundColor: notif.isError ? '#fee2e2' : '#fef9c3',
                                        borderRadius: '8px',
                                        fontSize: '0.9rem'
                                    }}>
                                        {notif.message}
                                    </li>
                                ))
                            )}
                        </ul>
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};


export default Notification;
